import React, { useCallback, useEffect, useState } from "react";
import api from "utils/api";
import { useTeamLocale } from "scenes/team/TeamLocaleContext";
import { formatEuro } from "scenes/team/formatters";
import GoMessagesGuide from "./GoMessagesGuide";

const formatWhen = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPercent = (value) =>
  `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value || 0)} %`;

const UmsatzTab = () => {
  const { t, locale } = useTeamLocale();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await api.get("/employee/umsatz");
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.message || t("umsatz.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || { grossCents: 0, employeeCents: 0, count: 0 };
  const entries = data?.entries || [];

  return (
    <div className="team-umsatz" role="tabpanel" aria-label="Umsatz">
      <div className="employee-umsatz-heading">
        <p className="team-lead">{t("umsatz.employeeLead")}</p>
        <GoMessagesGuide />
      </div>
      {loading ? (
        <div className="team-empty" role="status">{t("common.loading")}</div>
      ) : (
        <>
          {error && <div className="auth-alert is-error">{error}</div>}

          <div className="employee-umsatz-summary">
            <div className="employee-umsatz-stat employee-umsatz-share">
              <span>{t("umsatz.yourShare")}</span>
              <strong>{formatEuro(totals.employeeCents, locale)}</strong>
            </div>
            <div className="employee-umsatz-stat">
              <span>{t("umsatz.totalGross")}</span>
              <strong>{formatEuro(totals.grossCents, locale)}</strong>
            </div>
            <div className="employee-umsatz-stat employee-umsatz-commission">
              <span>{t("umsatz.yourRate")}</span>
              <strong>{formatPercent(data?.commissionPercent)}</strong>
            </div>
          </div>
          <p className="team-muted">{t("umsatz.yourRateHint")}</p>

          <h2 className="team-umsatz-list-title">{t("umsatz.listTitle")}</h2>
          {entries.length === 0 ? (
            <p className="team-muted">{t("umsatz.empty")}</p>
          ) : (
            <div className="team-table-wrap">
              <table className="team-table">
                <thead>
                  <tr>
                    <th>{t("umsatz.colCompany")}</th>
                    <th>{t("umsatz.colDate")}</th>
                    <th>{t("umsatz.colGross")}</th>
                    <th>{t("umsatz.colRate")}</th>
                    <th>{t("umsatz.colShare")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.companyName}</td>
                      <td>{formatWhen(entry.occurredAt)}</td>
                      <td>{formatEuro(entry.grossCents, locale)}</td>
                      <td>{formatPercent(entry.commissionPercent)}</td>
                      <td>{formatEuro(entry.employeeCents, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UmsatzTab;
